"use client";
import React from "react";

export function Sparkle({ size = 28, color = "var(--ink)", style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ display: "block", ...style }}>
      <path
        d="M50 0 C52 40 60 48 100 50 C60 52 52 60 50 100 C48 60 40 52 0 50 C40 48 48 40 50 0 Z"
        fill={color}
      />
    </svg>
  );
}

export function Star({ size = 22, color = "var(--ink)", style = {} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "block", ...style }}>
      <path
        d="M12 2l2.9 6.6L22 9.3l-5.2 4.7L18.3 21 12 17.3 5.7 21 7.2 14 2 9.3l7.1-.7L12 2z"
        fill={color}
      />
    </svg>
  );
}

export function Squiggle({ w = 260, h = 18, color = "var(--pink)", stroke = 4, style = {} }) {
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", ...style }}>
      <path
        d={`M 6 ${h - 5} Q ${w * 0.15} 2, ${w * 0.3} ${h - 5} T ${w * 0.6} ${h - 5} T ${w * 0.9} ${h - 5}`}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function WaveRule({ w = 400, h = 14, color = "var(--ink)", style = {} }) {
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", ...style }}>
      <path
        d={`M 0 ${h / 2} Q ${w * 0.08} 0, ${w * 0.16} ${h / 2} T ${w * 0.32} ${h / 2} T ${w * 0.48} ${h / 2} T ${w * 0.64} ${h / 2} T ${w * 0.8} ${h / 2} T ${w * 0.96} ${h / 2} L ${w} ${h / 2}`}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
      />
    </svg>
  );
}

export function Stamp({
  size = 130,
  text = "quarkle · certified · cozy ·",
  color = "var(--ink)",
  innerText = "EST·26",
  style = {},
}) {
  const cx = size / 2, cy = size / 2, r = size / 2 - 12;
  const id = "stamp-" + React.useId().replace(/[:]/g, "");
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: "block", animation: "qv6-spin-slow 40s linear infinite", ...style }}
    >
      <defs>
        <path id={id} d={`M ${cx} ${cy} m -${r},0 a ${r},${r} 0 1,1 ${r * 2},0 a ${r},${r} 0 1,1 -${r * 2},0`} />
      </defs>
      <circle cx={cx} cy={cy} r={r + 4} fill="none" stroke={color} strokeWidth="1" />
      <circle cx={cx} cy={cy} r={r - 8} fill="none" stroke={color} strokeWidth="1" />
      <text
        fill={color}
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        <textPath href={`#${id}`} startOffset="0">
          {text.repeat(3)}
        </textPath>
      </text>
      <text
        x={cx}
        y={cy + 4}
        textAnchor="middle"
        fill={color}
        style={{ fontFamily: "'Gambarino', serif", fontStyle: "italic", fontSize: 15 }}
      >
        {innerText}
      </text>
    </svg>
  );
}

export function Ribbon({ children, color = "var(--pink)", fg = "#fff", rotate = -2, style = {} }) {
  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        background: color,
        color: fg,
        padding: "8px 20px 9px",
        fontFamily: "'Gambarino', serif",
        fontStyle: "italic",
        fontSize: 18,
        transform: `rotate(${rotate}deg)`,
        boxShadow: "3px 3px 0 var(--ink)",
        border: `1px solid ${fg}`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function TapeSticker({ children, color = "var(--lime)", rotate = -3, style = {} }) {
  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        background: color,
        color: "#1A1410",
        padding: "8px 14px",
        border: "1px solid #1A1410",
        transform: `rotate(${rotate}deg)`,
        boxShadow: "3px 3px 0 var(--ink)",
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.14em",
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: -8,
          left: 14,
          width: 34,
          height: 14,
          background: "rgba(255,255,255,0.55)",
          border: "1px dashed rgba(0,0,0,0.25)",
          transform: "rotate(-8deg)",
        }}
      />
      {children}
    </div>
  );
}

export function Ticket({ children, style = {} }) {
  return (
    <div
      style={{
        position: "relative",
        background: "var(--paper-3)",
        border: "1px solid var(--ink)",
        padding: "14px 18px",
        borderRadius: 6,
        boxShadow: "4px 4px 0 var(--ink)",
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: -7,
          top: "50%",
          width: 14,
          height: 14,
          borderRadius: 999,
          background: "var(--paper)",
          border: "1px solid var(--ink)",
          transform: "translateY(-50%)",
        }}
      />
      <span
        aria-hidden
        style={{
          position: "absolute",
          right: -7,
          top: "50%",
          width: 14,
          height: 14,
          borderRadius: 999,
          background: "var(--paper)",
          border: "1px solid var(--ink)",
          transform: "translateY(-50%)",
        }}
      />
      {children}
    </div>
  );
}

export function PlaceholderSwatch({ w = 200, h = 140, label = "artwork", style = {} }) {
  return (
    <div
      style={{
        width: w,
        height: h,
        position: "relative",
        background: `repeating-linear-gradient(45deg, var(--paper-2) 0 10px, var(--paper-3) 10px 20px)`,
        border: "1px solid var(--ink)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
    >
      <span className="mono" style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.14em" }}>
        [{label}]
      </span>
    </div>
  );
}

export function CornerPin({ children, color = "var(--pink)", style = {} }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 12px",
        background: "var(--paper-3)",
        border: "1px solid var(--ink)",
        borderRadius: 999,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: "0.14em",
        ...style,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          background: color,
          animation: "qv6-qpulse 1.5s infinite",
        }}
      />
      {children}
    </span>
  );
}

export function BaselineGrid({ w = 800, h = 600, cell = 24, color = "var(--ink)", opacity = 0.08, style = {} }) {
  const verts = [];
  const horz = [];
  for (let x = 0; x <= w; x += cell)
    verts.push(<line key={"v" + x} x1={x} y1={0} x2={x} y2={h} stroke={color} strokeWidth="0.5" />);
  for (let y = 0; y <= h; y += cell)
    horz.push(<line key={"h" + y} x1={0} y1={y} x2={w} y2={y} stroke={color} strokeWidth="0.5" />);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", opacity, ...style }}>
      {verts}
      {horz}
    </svg>
  );
}

export function OutlineDisplay({
  text = "quarkle",
  size = 200,
  color = "var(--ink)",
  strokeW = 1.2,
  italic = true,
  style = {},
}) {
  return (
    <span
      style={{
        fontFamily: "'DM Serif Display','Times New Roman', serif",
        fontStyle: italic ? "italic" : "normal",
        fontSize: size,
        lineHeight: 0.82,
        letterSpacing: "-0.03em",
        color: "transparent",
        WebkitTextStrokeWidth: strokeW,
        WebkitTextStrokeColor: color,
        display: "inline-block",
        ...style,
      }}
    >
      {text}
    </span>
  );
}

export function Asterism({ size = 28, color = "var(--ink)", style = {} }) {
  const s = size / 3;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: size / 8, ...style }}>
      <Star size={s} color={color} />
      <span style={{ display: "inline-flex", gap: size / 8 }}>
        <Star size={s} color={color} />
        <Star size={s} color={color} />
      </span>
    </span>
  );
}
